import { LinkOutlined, SendOutlined } from '@ant-design/icons';
import { Button, Input, Spin, Tooltip, Typography, theme } from 'antd';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import type { ConversationMessageDto } from '@clh/api-client';
import { useConfig } from '@clh/ui';

import { useToast } from '../hooks/use-toast';

import { ChatMessages } from './chat-messages';
import humlaBotSrc from './humla-bot.png';

const { TextArea } = Input;

const { Text } = Typography;

let messageIdCounter = 0;

interface ChatProps {
    chatToken: string | null;
    onConversationChange?: (conversationId: string) => void;
}

export default function Chat({ chatToken, onConversationChange }: ChatProps) {
    const config = useConfig();
    const { notification } = useToast();
    const { conversationId: urlConversationId } = useParams<{
        conversationId: string;
    }>();

    const {
        token: { colorBgContainer, borderRadiusLG },
    } = theme.useToken();
    const [messages, setMessages] = useState<ConversationMessageDto[]>([]);
    const [input, setInput] = useState('');
    const [copied, setCopied] = useState(false);
    const [isStreaming, setIsStreaming] = useState(false);
    const [loading, setLoading] = useState(!!urlConversationId);
    const [conversationId, setConversationId] = useState<string | null>(
        urlConversationId ?? null
    );
    const [conversationTitle, setConversationTitle] = useState<string | null>(
        null
    );
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const abortControllerRef = useRef<AbortController | null>(null);
    const textAreaRef = useRef<ReturnType<typeof Input.TextArea> | null>(null);

    // Load existing conversation from URL
    useEffect(() => {
        if (!urlConversationId || !chatToken) return;
        let cancelled = false;

        async function load() {
            const res = await fetch(
                `${config.apiBaseUrl}/admin-chat/conversations/${urlConversationId}`,
                { headers: { Authorization: `Bearer ${chatToken}` } }
            );
            if (!res.ok) {
                if (res.status === 404) {
                    notification.error({
                        message:
                            'Conversation not found. It may have been deleted.',
                        duration: 5,
                    });
                } else {
                    notification.error({
                        message: `Failed to load conversation (${res.status})`,
                        duration: 5,
                    });
                }
                return;
            }
            const data = await res.json();
            if (cancelled) return;

            setMessages(data.messages);
            setConversationId(data.id);
            if (data.title) setConversationTitle(data.title);
        }

        load()
            .catch(() => {
                notification.error({
                    message: 'Failed to load conversation',
                    duration: 5,
                });
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [urlConversationId, chatToken, config.apiBaseUrl]);

    const scrollToBottom = useCallback(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, []);

    useEffect(() => {
        scrollToBottom();
    }, [messages, scrollToBottom]);

    useEffect(() => {
        requestAnimationFrame(() => {
            (textAreaRef.current as any)?.focus();
        });
    }, []);

    const sendMessage = async () => {
        const trimmed = input.trim();
        if (!trimmed || isStreaming || !chatToken) return;

        const userMessage: ConversationMessageDto = {
            id: `user-${++messageIdCounter}`,
            role: 'user',
            content: trimmed,
        };
        const updatedMessages = [...messages, userMessage];
        setMessages(updatedMessages);
        setInput('');
        setIsStreaming(true);

        const assistantMessage: ConversationMessageDto = {
            id: `assistant-${++messageIdCounter}`,
            role: 'assistant',
            content: '',
            toolCalls: [],
        };
        const assistantIndex = updatedMessages.length;
        setMessages((prev) => [...prev, assistantMessage]);

        const abortController = new AbortController();
        abortControllerRef.current = abortController;

        try {
            const apiMessages = updatedMessages.map((m) => ({
                role: m.role,
                content: m.content,
            }));

            const response = await fetch(`${config.apiBaseUrl}/admin-chat`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${chatToken}`,
                },
                body: JSON.stringify({
                    messages: apiMessages,
                    source: 'ADMIN_WEB',
                    ...(conversationId && { conversationId }),
                }),
                signal: abortController.signal,
            });

            if (!response.ok) {
                throw new Error(
                    `HTTP ${response.status}: ${response.statusText}`
                );
            }

            const reader = response.body?.getReader();
            if (!reader) throw new Error('No response body');

            const decoder = new TextDecoder();
            let buffer = '';

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;

                buffer += decoder.decode(value, { stream: true });

                const parts = buffer.split('\n\n');
                buffer = parts.pop() ?? '';

                for (const part of parts) {
                    if (!part.trim()) continue;

                    const lines = part.split('\n');
                    let eventType = '';
                    let eventData = '';

                    for (const line of lines) {
                        if (line.startsWith('event: ')) {
                            eventType = line.slice(7);
                        } else if (line.startsWith('data: ')) {
                            eventData = line.slice(6);
                        }
                    }

                    if (!eventType || !eventData) continue;

                    let data: any;
                    try {
                        data = JSON.parse(eventData);
                    } catch {
                        continue;
                    }

                    switch (eventType) {
                        case 'text_delta':
                            setMessages((prev) => {
                                const updated = [...prev];
                                const assistant = updated[assistantIndex];
                                if (assistant) {
                                    updated[assistantIndex] = {
                                        ...assistant,
                                        content: assistant.content + data.text,
                                    };
                                }
                                return updated;
                            });
                            break;

                        case 'tool_call_start':
                            setMessages((prev) => {
                                const updated = [...prev];
                                const assistant = updated[assistantIndex];
                                if (assistant) {
                                    updated[assistantIndex] = {
                                        ...assistant,
                                        toolCalls: [
                                            ...(assistant.toolCalls ?? []),
                                            {
                                                id: data.id,
                                                name: data.name,
                                                input: data.input,
                                            },
                                        ],
                                    };
                                }
                                return updated;
                            });
                            break;

                        case 'tool_call_result':
                            setMessages((prev) => {
                                const updated = [...prev];
                                const assistant = updated[assistantIndex];
                                if (assistant?.toolCalls) {
                                    const calls = [...assistant.toolCalls];
                                    const lastCallOfName = calls
                                        .slice()
                                        .reverse()
                                        .find((tc) => tc.name === data.name);
                                    if (lastCallOfName) {
                                        lastCallOfName.result = data.result;
                                    }
                                    updated[assistantIndex] = {
                                        ...assistant,
                                        toolCalls: calls,
                                    };
                                }
                                return updated;
                            });
                            break;

                        case 'done':
                            if (data.conversationId) {
                                setConversationId(data.conversationId);
                                window.history.replaceState(
                                    null,
                                    '',
                                    `/chat/${data.conversationId}`
                                );
                                onConversationChange?.(data.conversationId);
                            }
                            if (data.title) {
                                setConversationTitle(data.title);
                            }
                            break;

                        case 'error':
                            setMessages((prev) => {
                                const updated = [...prev];
                                const assistant = updated[assistantIndex];
                                if (assistant) {
                                    updated[assistantIndex] = {
                                        ...assistant,
                                        content:
                                            assistant.content +
                                            `\n\nError: ${data.message}`,
                                    };
                                }
                                return updated;
                            });
                            break;
                    }
                }
            }
        } catch (error) {
            if ((error as Error).name !== 'AbortError') {
                const errorMessage =
                    error instanceof Error ? error.message : 'Unknown error';
                setMessages((prev) => {
                    const updated = [...prev];
                    const assistant = updated[assistantIndex];
                    if (assistant) {
                        updated[assistantIndex] = {
                            ...assistant,
                            content:
                                assistant.content +
                                `\n\nError: ${errorMessage}`,
                        };
                    }
                    return updated;
                });
            }
        } finally {
            setIsStreaming(false);
            abortControllerRef.current = null;
            requestAnimationFrame(() => {
                (textAreaRef.current as any)?.focus();
            });
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    };

    if (loading) {
        return (
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100%',
                }}
            >
                <Spin size="large" />
            </div>
        );
    }

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                width: '100%',
                background: '#f0f2f5',
            }}
        >
            <div
                style={{
                    padding: '12px 24px',
                    borderBottom: '1px solid #e8e8e8',
                    background: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                }}
            >
                <Typography.Title level={4} style={{ margin: 0 }}>
                    {conversationTitle ?? 'New Conversation'}
                </Typography.Title>
                {conversationId && (
                    <Tooltip title={copied ? 'Copied!' : 'Copy share link'}>
                        <Button
                            icon={<LinkOutlined />}
                            onClick={() => {
                                navigator.clipboard
                                    .writeText(window.location.href)
                                    .then(() => {
                                        setCopied(true);
                                        setTimeout(
                                            () => setCopied(false),
                                            2000
                                        );
                                    })
                                    .catch(() => {
                                        notification.error({
                                            message:
                                                'Failed to copy link to clipboard',
                                            duration: 3,
                                        });
                                    });
                            }}
                        />
                    </Tooltip>
                )}
            </div>
            {/* Messages area */}
            <div
                style={{
                    flex: 1,
                    overflowY: 'auto',
                    padding: '24px 24px 0',
                }}
            >
                <div style={{ maxWidth: 800, margin: '0 auto' }}>
                    {messages.length === 0 && (
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                height: 'calc(100vh - 190px)',
                                gap: 12,
                            }}
                        >
                            <img
                                src={humlaBotSrc}
                                alt="Humla Bot"
                                style={{
                                    width: 80,
                                    height: 80,
                                    borderRadius: '50%',
                                }}
                            />
                            <Text type="secondary" style={{ fontSize: 16 }}>
                                Ask a question about Humla platform data
                            </Text>
                            <Text type="secondary" style={{ fontSize: 13 }}>
                                e.g. "How many active profiles are there?" or
                                "Show me upcoming shifts"
                            </Text>
                        </div>
                    )}

                    <ChatMessages
                        messages={messages}
                        isStreaming={isStreaming}
                    />
                    <div ref={messagesEndRef} />
                </div>
            </div>

            {/* Input area */}
            <div
                style={{
                    padding: '16px 24px 24px',
                    background: '#f0f2f5',
                }}
            >
                <div
                    style={{
                        maxWidth: 800,
                        margin: '0 auto',
                        display: 'flex',
                        gap: 8,
                        background: colorBgContainer,
                        padding: 8,
                        borderRadius: borderRadiusLG,
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                    }}
                >
                    <TextArea
                        ref={textAreaRef as any}
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder="Ask about profiles, shifts, facilities..."
                        autoSize={{ minRows: 1, maxRows: 4 }}
                        variant="borderless"
                        style={{ flex: 1, resize: 'none' }}
                    />
                    <Button
                        type="primary"
                        icon={<SendOutlined />}
                        onClick={sendMessage}
                        disabled={isStreaming || !input.trim()}
                        style={{ alignSelf: 'flex-end' }}
                    />
                </div>
            </div>
        </div>
    );
}
